// @flow
import { useMutation, useQuery } from '@apollo/react-hooks';
import Button from '@atlaskit/button';
import Form, { Field } from '@atlaskit/form';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import Select from '@atlaskit/select/Select';
import Spinner from '@atlaskit/spinner';
import React, { useState } from 'react';
import styled from 'styled-components';
import { UPDATE_WIDGET_CONFIG } from '../../graphql/mutations';
import { SERVICE_DESK_PROJECTS, WIDGETS_QUERY } from '../../graphql/queries';

const FieldsWrapper = styled.div`
    padding-bottom: 48px;
`;

type Props = {
    setIsOpen: (isOpen: boolean) => void,
};

function LinkServiceDeskModal({ setIsOpen, widget }: Props) {
    // form state
    const [project, setProject] = useState(null);
    const [issueType, setIssueType] = useState(null);
    const [loading, setLoading] = useState(false);

    function getIssueTypesOption(issueTypeOption) {
        return {
            value: issueTypeOption.id,
            label: issueTypeOption.name,
        };
    }

    function getIssueTypesOptions(issueTypes) {
        return issueTypes.map(getIssueTypesOption);
    }

    function getServiceDeskProjectsOption(serviceDeskProject) {
        return {
            value: serviceDeskProject.id,
            label: serviceDeskProject.name,
            issueTypes: getIssueTypesOptions(serviceDeskProject.issueTypes),
        };
    }

    function getServiceDeskProjectsOptions(serviceDeskProjects) {
        return serviceDeskProjects.map(getServiceDeskProjectsOption);
    }

    // graphql
    const { data: serviceDeskProjects, loading: serviceDeskProjectsLoading } = useQuery(SERVICE_DESK_PROJECTS);
    const [updateWidgetConfig] = useMutation(UPDATE_WIDGET_CONFIG, {
        update(cache, { data: { updateWidgetConfig: updateWidgetConfigData } }) {
            const { widgets } = cache.readQuery({ query: WIDGETS_QUERY });
            const newWidgets = widgets.map((currWidget) => {
                if (currWidget.id === updateWidgetConfigData.widgetId) {
                    const editedWidget = JSON.parse(JSON.stringify(currWidget));
                    editedWidget.widgetConfig = updateWidgetConfigData;
                    return editedWidget;
                }
                return currWidget;
            });
            cache.writeQuery({
                query: WIDGETS_QUERY,
                data: { widgets: newWidgets },
            });
        },
    });
    let serviceDeskProjectsOptions = [];
    if (!serviceDeskProjectsLoading) {
        serviceDeskProjectsOptions = getServiceDeskProjectsOptions(serviceDeskProjects.serviceDeskProjects);
    }

    function setFormFields() {
        if (
            (project &&
                project.value === widget.widgetConfig.jiraServiceDeskId &&
                issueType &&
                issueType.value === widget.widgetConfig.jiraIssueTypeId) ||
            !widget.widgetConfig.jiraServiceDeskId ||
            !widget.widgetConfig.jiraIssueTypeId
        ) {
            return;
        }
        if (serviceDeskProjectsOptions.length > 0) {
            const projectOption = serviceDeskProjectsOptions.find(
                (el) => el.value === widget.widgetConfig.jiraServiceDeskId,
            );
            setProject(projectOption);
            setIssueType(projectOption.issueTypes.find((el) => el.value === widget.widgetConfig.jiraIssueTypeId));
        }
    }

    const onProjectSelect = (value) => {
        if (!project || (project && project.id !== value.id)) {
            if (value.issueTypes.length > 0) {
                setIssueType(value.issueTypes[0]);
            } else {
                setIssueType(null);
            }
        }
        setProject(value);
    };

    const _submitForm = async (data) => {
        setLoading(true);
        const payload = {
            widgetId: widget.id,
            jiraServiceDeskId: data['service-desk-project'].value,
            jiraIssueTypeId: data['issue-type'].value,
        };
        await updateWidgetConfig({ variables: { payload } });
        setLoading(false);
        setIsOpen(false);
    };

    setFormFields();

    return (
        <ModalTransition>
            <Modal shouldScrollInViewport onClose={() => setIsOpen(false)}>
                <Form onSubmit={_submitForm}>
                    {({ formProps }) => (
                        <form {...formProps}>
                            <ModalHeader>
                                <ModalTitle>Link to Jira Service Desk project</ModalTitle>
                            </ModalHeader>

                            <ModalBody>
                                <FieldsWrapper>
                                    <p>
                                        User feedback sent from widget is raised as a Jira service desk issue in
                                        following project…
                                    </p>
                                    <Field
                                        name="service-desk-project"
                                        label="Service desk project"
                                        id="service-desk-project"
                                        defaultValue={project}
                                        isRequired
                                    >
                                        {({ fieldProps }) => (
                                            <Select
                                                {...fieldProps}
                                                options={serviceDeskProjectsOptions}
                                                placeholder="Select Service Desk project"
                                                onChange={(value) => {
                                                    fieldProps.onChange(value);
                                                    onProjectSelect(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                    <Field
                                        name="issue-type"
                                        label="Issue type"
                                        id="issue-type"
                                        defaultValue={issueType}
                                        isRequired
                                    >
                                        {({ fieldProps }) => (
                                            <Select
                                                {...fieldProps}
                                                options={project ? project.issueTypes : []}
                                                placeholder="Select Issue type"
                                                onChange={(value) => {
                                                    fieldProps.onChange(value);
                                                    setIssueType(value);
                                                }}
                                            />
                                        )}
                                    </Field>
                                </FieldsWrapper>{' '}
                            </ModalBody>
                            <ModalFooter>
                                <Button onClick={() => setIsOpen(false)}>Cancel</Button>
                                <Button
                                    type="submit"
                                    isDisabled={loading || !project || !issueType}
                                    appearance="primary"
                                    iconAfter={loading ? <Spinner size="small" /> : null}
                                >
                                    Save
                                </Button>
                            </ModalFooter>
                        </form>
                    )}
                </Form>
            </Modal>
        </ModalTransition>
    );
}

export default LinkServiceDeskModal;
